import styled from 'styled-components'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'

import React, { useEffect } from 'react'
import { ModalProps } from 'src/components/Modal'
import { ModalSpace } from 'src/components/Modal/provider'
import RenderIf from 'src/components/RenderIf'
import { Button } from '../Button'

const Rop04ModalView = ({ modalData, onSubmitInternal, onCancelInternal }: ModalProps) => {
  const { button, contents } = modalData as ModalSpace.ModalData
  const modalVars = {
    start: {
      opacity: 0,
      y: 100,
    },
    end: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        duration: 0.5,
      },
    },
    finish: {
      opacity: 0,
      y: -100,
      transition: {
        type: 'spring',
        duration: 0.5,
      },
    },
  }

  let isReady = false
  const timer = setTimeout(() => {
    isReady = true
  }, 100)

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isReady) {
        onSubmitInternal()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const active = document.activeElement as HTMLElement | null
    const originalOverflow = window.getComputedStyle(document.body).overflow
    const originalPosition = window.getComputedStyle(document.body).position
    const scrollY = window.scrollY

    if (active && typeof active.blur === 'function') {
      active.blur()
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.top = `-${scrollY}px`
    }

    return () => {
      document.body.style.overflow = originalOverflow
      document.body.style.position = originalPosition
      document.body.style.width = ''
      document.body.style.top = ''
      window.scrollTo(0, scrollY)
    }
  }, [])

  return createPortal(
    <React.Fragment>
      <AnimatePresence>
        <ModalBackGroundWrap />
      </AnimatePresence>
      <ModalWrap>
        <AnimatePresence>
          <ModalInner variants={modalVars} initial="start" animate="end" exit="finish">
            <ModalTitle $active={!!contents?.title}> {contents?.title}</ModalTitle>
            <ModalContents $active={!!contents?.desc}>{contents?.desc}</ModalContents>
            {contents?.children}
            <RenderIf when={!!button}>
              <ModalButtonWrap>
                <RenderIf when={!!button?.cancel}>
                  <Button.Default onClick={onCancelInternal}>{button?.cancel?.text}</Button.Default>
                </RenderIf>
                <RenderIf when={!!button?.submit}>
                  <Button.Primary onClick={onSubmitInternal}>{button?.submit?.text}</Button.Primary>
                </RenderIf>
              </ModalButtonWrap>
            </RenderIf>
          </ModalInner>
        </AnimatePresence>
      </ModalWrap>
    </React.Fragment>,
    document.body,
  )
}
export default Rop04ModalView

const ModalBackGroundWrap = styled(motion.div)`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(2.5px);
  -webkit-backdrop-filter: blur(2.5px);
`
const ModalWrap = styled.div`
  width: 100%;
  height: 100vh;
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  right: 0;
  margin: auto;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
`
const ModalInner = styled(motion.div)`
  width: 100%;
  min-width: 280px;
  max-width: 300px;
  padding: 20px;
  background-color: #fff;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: 12px;
`

const ModalTitle = styled.strong<{
  $active: boolean
}>`
  display: ${(props) => (props.$active ? ' block' : 'none')};
  text-align: center;
  word-break: keep-all;
  color: #25282e;
  font-family: Pretendard, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
`
const ModalContents = styled.p<{
  $active: boolean
}>`
  display: ${(props) => (props.$active ? 'block' : 'none')};
  word-break: keep-all;
  white-space: break-spaces;
  color: #313338;
  text-align: center;
  font-family: Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`

const ModalButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 8px;

  & button {
    height: 48px !important;
    flex: 1 !important;
    letter-spacing: normal;
  }
`

export const ModalInnerScroll = styled.div`
  width: 100%;
  max-height: 212px;
  overflow: hidden;
  overflow-y: auto;
  background-color: var(--black-2);
  padding: 6px;
  border-radius: 12px;
  line-height: 1.4;
  margin-bottom: 16px;
  text-align: left;
  white-space: break-spaces;
`

export const ModalInnerText = styled.p`
  word-break: keep-all;
  line-height: 1.4;
`
